import React, { useState, useEffect } from 'react';
import { getJson } from '@/lib/http';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, Lock, Unlock, AlertTriangle, CheckCircle, Eye, CircleSlash } from 'lucide-react';
import { useAuth } from '@/lib/auth-store';
import { toast } from 'sonner';

interface PackLockingPanelProps {
  packId: number;
  onLockStatusChange?: (locked: boolean, versionId?: number) => void;
  showIds?: boolean;
}

interface PackPreview {
  summary: {
    storeCount: number;
    formatCount: number;
    unitCount: number;
    operators: Array<{
      id: string;
      name: string;
      count: number;
    }>;
    unresolvedCount: number;
  };
  diagnostics: {
    missingOperators: string[];
    noEligibleGroup: string[];
    policyConflicts: Array<{
      store_id: string;
      reason: string;
    }>;
    operatorAssignmentIssues?: string[];
    formatNameDrift?: string[];
    storeLabelDifferences?: string[];
  };
  paging: {
    page: number;
    pageSize: number;
    total: number;
  };
  items?: unknown[];
}

interface LockResult {
  success: boolean;
  message: string;
  versionId?: number;
  version?: number;
  error?: string;
  invalidStoreCount?: number;
  invalidStores?: Array<{
    storeId: number;
    storeName: string;
    reason: 'deleted' | 'archived' | 'retailer_mismatch';
  }>;
}

interface PackLockStatusResponse {
  versions: Array<{ id: number; status: string }>;
}

export const PackLockingPanel: React.FC<PackLockingPanelProps> = ({ 
  packId, 
  onLockStatusChange,
  showIds = false
}) => {
  const { state } = useAuth();
  const [packPreview, setPackPreview] = useState<PackPreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lockReason, setLockReason] = useState('');
  const [showLockForm, setShowLockForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [lockedVersionId, setLockedVersionId] = useState<number | null>(null);
  const [checking, setChecking] = useState(false);

  // Fetch pack preview on component mount
  useEffect(() => {
    if (state.status === 'authenticated') {
      fetchPackPreview();
      refreshStatus();
    }
    
    // Set up periodic refresh of lock status to keep UI in sync
    const interval = setInterval(() => {
      refreshStatus();
    }, 5000); // Check every 5 seconds
    
    return () => clearInterval(interval);
  }, [packId, state.status]);

  const fetchPackPreview = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/packs/${packId}/preview?page=1&pageSize=1`, {
        credentials: 'omit'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPackPreview(data);
        setError(null);
      } else {
        const errorData = await response.json();
        console.warn('Pack preview failed, but allowing pack locking anyway:', errorData);
        // Don't show preview error - just continue without preview for pack locking
        setPackPreview({
          summary: {
            storeCount: 0,
            formatCount: 0,
            unitCount: 0,
            operators: [],
            unresolvedCount: 0
          },
          items: [],
          diagnostics: {
            missingOperators: [],
            noEligibleGroup: [],
            policyConflicts: [],
            operatorAssignmentIssues: [],
            formatNameDrift: [],
            storeLabelDifferences: []
          },
          paging: {
            page: 1,
            pageSize: 50,
            total: 0
          }
        });
        setError(null);
      }
    } catch (err) {
      console.warn('Pack preview network error, but allowing pack locking anyway:', err);
      // Don't show network error - just continue without preview for pack locking
      setPackPreview({
        summary: {
          storeCount: 0,
          formatCount: 0,
          unitCount: 0,
          operators: [],
          unresolvedCount: 0
        },
        items: [],
        diagnostics: {
          missingOperators: [],
          noEligibleGroup: [],
          policyConflicts: [],
          operatorAssignmentIssues: [],
          formatNameDrift: [],
          storeLabelDifferences: []
        },
        paging: {
          page: 1,
          pageSize: 50,
          total: 0
        }
      });
      setError(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshStatus = async () => {
    setChecking(true);
    try {
      const data = await getJson<PackLockStatusResponse>(`/api/pack-locking/${packId}/lock-status`);
      const lockedVersion = data?.versions?.find((v: any) => v.status === 'locked');
      const nowLocked = !!lockedVersion;
      setIsLocked(nowLocked);
      onLockStatusChange?.(nowLocked, lockedVersion?.id);
    } catch (err) {
      console.error('Failed to refresh lock status', err);
    } finally {
      setChecking(false);
    }
  };

  // Compute lockability from preview data
  const canLock = packPreview ? 
    packPreview.summary.unresolvedCount === 0 &&
    packPreview.summary.storeCount > 0 &&
    packPreview.summary.unitCount > 0 : false;

  // Generate blockers list from preview data
  const blockers: string[] = [];
  if (packPreview) {
    if (packPreview.summary.storeCount === 0) blockers.push("No stores in draft");
    if (packPreview.summary.unitCount === 0) blockers.push("No format units in draft");
    if (packPreview.diagnostics.missingOperators?.length) blockers.push("Missing operators");
    if (packPreview.diagnostics.noEligibleGroup?.length) blockers.push("No eligible format group for some stores");
    if (packPreview.diagnostics.policyConflicts?.length) blockers.push("Policy conflicts present");
  }

  const handleLockPack = async () => {
    if (!lockReason.trim()) {
      setError('Please provide a reason for locking the pack');
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`/api/packs/${packId}/lock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify({ reason: lockReason.trim() })
      });

      const result: LockResult = await response.json();

      if (result.success) {
        setError(null);
        setLockReason('');
        setShowLockForm(false);
        setIsLocked(true);
        if (result.versionId) {
          setLockedVersionId(result.versionId);
        }
        // Refresh both preview data and lock status to ensure UI is up to date
        await Promise.all([
          fetchPackPreview(),
          refreshStatus()
        ]);
        onLockStatusChange?.(true, result.versionId || undefined);
        toast.success('Pack locked successfully');
      } else {
        const invalidStoreNames = Array.isArray(result.invalidStores)
          ? result.invalidStores.map((store) => store.storeName).filter(Boolean)
          : [];
        const errorMsg = result.error === 'PACK_INTEGRITY_ISSUES'
          ? [
              result.message || 'This pack contains stores that are no longer valid.',
              invalidStoreNames.length > 0 ? `Affected stores: ${invalidStoreNames.join(', ')}` : null,
            ].filter(Boolean).join(' ')
          : (result.error || 'Failed to lock pack');
        setError(errorMsg);
        toast.error(`Failed to lock pack: ${errorMsg}`);
      }
    } catch (err) {
      const errorMsg = 'Network error while locking pack';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnlockPack = async (versionId: number) => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/packs/${packId}/unlock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify({ versionId })
      });

      const result = await response.json();

      if (result.success) {
        setError(null);
        setIsLocked(false);
        setLockedVersionId(null);
        // Refresh both preview data and lock status to ensure UI is up to date
        await Promise.all([
          fetchPackPreview(),
          refreshStatus()
        ]);
        onLockStatusChange?.(false);
        toast.success('Pack unlocked successfully');
      } else {
        const errorMsg = result.error || 'Failed to unlock pack';
        setError(errorMsg);
        toast.error(`Failed to unlock pack: ${errorMsg}`);
      }
    } catch (err) {
      const errorMsg = 'Network error while unlocking pack';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !packPreview) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-6">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="ml-2">Loading pack preview...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-pack-locking-panel>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5" />
          Pack Locking
          {showIds && (
            <Badge variant="outline" className="text-xs font-mono">
              pack_id: {packId}
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="flex items-center justify-between">
          <span>Lock this pack to create an immutable version for campaign creation</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => {
              refreshStatus();
              fetchPackPreview();
            }}
            disabled={isLoading}
          >
            <Loader2 className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Lock Status */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-medium">Current Status:</span>
            <Badge variant={isLocked ? "default" : "secondary"} className="flex items-center gap-1">
              {isLocked ? <CheckCircle className="h-3 w-3 text-green-600" /> : <CircleSlash className="h-3 w-3 text-gray-500" />}
              {isLocked ? "Locked" : "Unlocked"}
            </Badge>
          </div>

          {packPreview && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Can Lock:</span>
                <Badge variant={canLock ? "default" : "destructive"}>
                  {canLock ? "Yes" : "No"}
                </Badge>
              </div>

              {/* Preview Summary */}
              <div className="grid grid-cols-2 gap-4 p-3 border rounded-lg bg-muted/50">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{packPreview.summary.storeCount}</div>
                  <div className="text-xs text-muted-foreground">Stores</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{packPreview.summary.unitCount}</div>
                  <div className="text-xs text-muted-foreground">Units</div>
                </div>
              </div>

              {/* Blockers */}
              {blockers.length > 0 && (
                <div className="space-y-2">
                  <span className="text-sm font-medium text-destructive">Blockers:</span>
                  <ul className="space-y-1">
                    {blockers.map((blocker, index) => (
                      <li key={index} className="text-sm text-destructive flex items-center gap-2">
                        <AlertTriangle className="h-3 w-3" />
                        {blocker}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Lock Form */}
        {!isLocked && canLock && (
          <div className="space-y-3">
            {!showLockForm ? (
              <Button 
                onClick={() => setShowLockForm(true)}
                className="w-full"
                disabled={isLoading}
              >
                <Lock className="h-4 w-4 mr-2" />
                Lock Pack
              </Button>
            ) : (
              <div className="space-y-3 p-4 border rounded-lg">
                <div>
                  <label className="text-sm font-medium">Reason for locking:</label>
                  <textarea
                    value={lockReason}
                    onChange={(e) => setLockReason(e.target.value)}
                    placeholder="e.g., Ready for campaign creation, final review complete"
                    className="w-full mt-1 p-2 border rounded-md"
                    rows={3}
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleLockPack}
                    disabled={isLoading || !lockReason.trim()}
                    className="flex-1"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Lock className="h-4 w-4 mr-2" />
                    )}
                    Confirm Lock
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setShowLockForm(false);
                      setLockReason('');
                    }}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lock Button Disabled State */}
        {!isLocked && !canLock && (
          <div className="space-y-3">
            <Button 
              className="w-full"
              disabled={true}
              title="Resolve draft blockers before locking."
            >
              <Lock className="h-4 w-4 mr-2" />
              Lock Pack
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              Resolve draft blockers before locking.
            </p>
          </div>
        )}

        {/* Unlock Button for Locked Packs */}
        {isLocked && lockedVersionId && (
          <div className="space-y-3">
            <Alert>
              <Lock className="h-4 w-4" />
              <AlertDescription>
                This pack is currently locked (Version ID: {lockedVersionId}). Unlocking will allow you to make changes and relock with updated data.
              </AlertDescription>
            </Alert>
            <Button 
              onClick={() => handleUnlockPack(lockedVersionId)}
              disabled={isLoading}
              variant="destructive"
              className="w-full"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Unlock className="h-4 w-4 mr-2" />
              )}
              Unlock Pack
            </Button>
          </div>
        )}

        {/* Success Message */}
        {!isLocked && canLock && blockers.length === 0 && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              This pack is ready to be locked. Locking will create an immutable version that can be used for campaign creation.
            </AlertDescription>
          </Alert>
        )}

        {/* Refresh Button */}
        <div className="flex justify-center">
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchPackPreview}
            disabled={isLoading}
          >
            <Eye className="h-4 w-4 mr-2" />
            Refresh Preview
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
